/**
 * 配列取込の呼び出し口。Worker が使えればそちらで、使えなければメインスレッドで走らせる。
 */

import {
  detectImportFormat,
  runGeneAnalysisImport,
  type GeneAnalysisImportDoneMessage,
  type GeneAnalysisImportProgressMessage,
  type GeneAnalysisImportRequest,
  type GeneAnalysisImportResponse,
} from '../workers/geneAnalysisImportWorker';

export type { GeneAnalysisImportDoneMessage, GeneAnalysisImportProgressMessage };
export { detectImportFormat };

export interface ImportSequenceFileParams {
  projectId: string;
  file: File;
  onProgress?: (progress: GeneAnalysisImportProgressMessage) => void;
  /** テストや Worker 非対応環境でメインスレッドに寄せる */
  forceMainThread?: boolean;
}

export function importSequenceFile(
  params: ImportSequenceFileParams
): Promise<GeneAnalysisImportDoneMessage> {
  const request: GeneAnalysisImportRequest = {
    type: 'IMPORT',
    projectId: params.projectId,
    file: params.file,
    format: detectImportFormat(params.file.name),
  };

  if (params.forceMainThread || typeof Worker === 'undefined') {
    return runGeneAnalysisImport(request, params.onProgress);
  }

  return new Promise((resolve, reject) => {
    let worker: Worker;
    try {
      worker = new Worker(new URL('../workers/geneAnalysisImportWorker.ts?worker', import.meta.url), {
        type: 'module',
      });
    } catch {
      // Worker を作れない環境（古いブラウザ・テスト）はメインスレッドへ退避
      runGeneAnalysisImport(request, params.onProgress).then(resolve, reject);
      return;
    }

    const finish = (action: () => void) => {
      worker.terminate();
      action();
    };

    worker.onmessage = (event: MessageEvent<GeneAnalysisImportResponse>) => {
      const message = event.data;
      if (message.type === 'PROGRESS') {
        params.onProgress?.(message);
        return;
      }
      if (message.type === 'DONE') {
        finish(() => resolve(message));
        return;
      }
      finish(() => reject(new Error(message.message)));
    };
    worker.onerror = (event) => {
      finish(() => reject(new Error(event.message || '取込 Worker でエラーが発生しました')));
    };

    worker.postMessage(request);
  });
}
